<template>
  <div>
    <Header>
      基本情報
    </Header>
    <CustomValidationObserver
      v-slot="{ handleSubmit }"
      ref="vObserver"
      :initialValues="formValues"
    >
      <div class="pt-2 pb-4 mx-3">
        <StepBar :maxValue="4" :currentValue="1" class="mb-3" />
        <PersonalInfo
          v-model="inputValues"
          :disable-v1-user-information="hasV1UserAccount"
          :showsWorkerService="isShownWorkerService"
          :fromRegister="true"
        />
      </div>
      <BottomFormActionButtons
        @submit="handleSubmit(onSubmit)"
        hideSkipButton
      />
    </CustomValidationObserver>

    <BlockBack />
  </div>
</template>

<script lang="ts">
import { accountService } from "@/app/services/AccountService";
import { eventService } from "@/app/services/EventService";
import StepBar from "@/components/molecules/other/StepBar.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import Header from "@/components/organisms/common/Header.vue";
import PersonalInfo from "@/components/organisms/common/PersonalInfo.vue";
import BottomFormActionButtons from "@/components/organisms/register/BottomFormActionButtons.vue";
import { WORKER_SERVICE_ID_CHILDCARE } from "@/const/workerService";
import { sendAdjustEvent } from "@/plugins/adjust";
import CustomValidationObserver from "@/plugins/CustomValidationObserver.vue";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import { authStore } from "@/store/auth";
import { masterStore } from "@/store/master";
import { PersonalInfoQueueBody } from "@/types/user/RequestVerificationQueueBody";
import { splitDate } from "@/utils/date";
import { routerPush } from "@/utils/routerPush";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Component, Hook, Vue, toNative } from "vue-facing-decorator";
import { NavigationGuardNext, RouteLocation } from "vue-router";

@Component({
  components: {
    Header,
    PersonalInfo,
    BlockBack,
    BottomFormActionButtons,
    StepBar
  }
})
class RegisterPersonalInfo extends Vue {
  inputValues: PersonalInfoQueueBody = {
    firstName: "",
    lastName: "",
    firstNameKana: "",
    lastNameKana: "",
    sex: "2",
    birthDate: null,
    postalCode: "",
    prefecture: null,
    locality: null,
    address1: "",
    address2: "",
    email: "",
    hasRegisteredPersonalInfo: false,
    workerService: undefined
  };
  isSkippedPage = false;

  get formValues() {
    // 会員登録なので値は取得できないはずだが、created でデータを取得～反映しているため分割処理は用意しておく
    const [birthDateYear, birthDateMonth, birthDateDay] = splitDate(
      this.inputValues.birthDate,
      "-"
    );

    return {
      firstName: this.inputValues.firstName,
      lastName: this.inputValues.lastName,
      firstNameKana: this.inputValues.firstNameKana,
      lastNameKana: this.inputValues.lastNameKana,
      sex: this.inputValues.sex,
      birthDate: this.inputValues.birthDate,
      birthDateYear,
      birthDateMonth,
      birthDateDay,
      email: this.inputValues.email,
      postalCode: this.inputValues.postalCode,
      prefecture: this.inputValues.prefecture,
      locality: this.inputValues.locality,
      address1: this.inputValues.address1,
      address2: this.inputValues.address2,
      workerService: this.inputValues.workerService
    };
  }

  @Hook
  beforeRouteEnter(
    to: RouteLocation,
    from: RouteLocation,
    next: NavigationGuardNext
  ) {
    next(vm => {
      const registerPersonalInfo = vm as RegisterPersonalInfo;
      // RegisterBusinessInfoから遷移した場合はスキップ処理をしない
      if (from.name !== "RegisterBusinessInfo") {
        registerPersonalInfo.isSkippedPage = true;
      }
    });
  }
  async created() {
    this.$loading.show();
    try {
      const userData = await accountService.fetchUserData();
      // ページを戻るなどで初回認証送信後にこのページに遷移した場合、強制的にマイページにリダイレクトする
      if (userData.hasRegisteredProfile) {
        await routerPush(this, { name: "Mypage" });
        return;
      }
      const firstBootSurveyV2 = authStore.firstBootSurveyV2;
      if (firstBootSurveyV2) {
        userData.workerService = firstBootSurveyV2.workerService;
        userData.prefecture = firstBootSurveyV2.prefecture;
        userData.locality = firstBootSurveyV2.locality;
      }
      this.inputValues = {
        ...(Object.fromEntries(
          Object.keys(this.inputValues).map(v => [
            v,
            userData[v as keyof typeof userData]
          ])
        ) as PersonalInfoQueueBody)
      };
    } catch (err) {
      await showErrorModal(this, err as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  mounted() {
    // 取得した値でフォームをリセットしておく
    // https://github.com/logaretm/vee-validate/issues/4602
    (this.$refs.vObserver as InstanceType<
      typeof CustomValidationObserver
    >).resetForm(this.formValues);
  }

  get hasV1UserAccount() {
    return authStore.user?.isV2Migrated !== true;
  }

  get isShownWorkerService() {
    return authStore.product.workerServices.length >= 2;
  }

  async onSubmit() {
    try {
      this.$loading.show();
      const body = {
        ...this.inputValues,
        // NOTE: notification_areas は現在は使われていない（https://gitlab.com/caitech/caisuke-front-v2/-/merge_requests/936）
        notificationAreas: masterStore.prefectures.map(
          // idが2桁になるようにゼロパディング
          option => option.id.toString().padStart(2, "0")
        ),
        hasRegisteredPersonalInfo: true,
        uuid: authStore.sessionId
      };

      // 業務カテゴリを表示していない場合（今のプロダクトで業務カテゴリが１つしか無い）は、今のプロダクトで取り扱える唯一の業務カテゴリをセットする
      if (!this.isShownWorkerService) {
        body.workerService = authStore.product.workerServices[0].id;
      }

      // TODO: 保育案件特需対応
      // 「保育:4」が設定されていたら強制的に「介護」にする
      if (Number(body.workerService) === WORKER_SERVICE_ID_CHILDCARE) {
        body.workerService = 1;
        eventService.registerEvent("analyze_childcare", "select_childcare");
      }

      await authStore.registerUserInfo(body);
      sendAdjustEvent("FINISH_INPUT_BASIC_INFO");
      sendFirebaseCustomEvent("finish_input_basic_info", {
        session_id: authStore.sessionId || "unknown"
      });
      await routerPush(this, { name: "RegisterSetAvatar" });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }
}

export default toNative(RegisterPersonalInfo);
</script>

<style lang="scss" scoped>
// TODO: 保育案件特需対応
// 横幅がきついのでここだけ余白を調整する
:deep(.radio-group) {
  margin-right: 0;
  margin-left: 0;
}

:deep(.radio-group__item) {
  margin: 0 4px 0 0;

  &:last-child {
    margin-right: 0;
  }
}

:deep(.app-radio__label--normal) {
  padding: 6px;
}
</style>
